import type { ParseKeys } from 'i18next'
import { createContext, use } from 'react'

import type { StoreZoneRead } from '@/client'
import { browserTimezone, type StoreZoneKind, type TimezoneSource } from '@/lib/timezone'

/** What a `TimezoneScope` (components/common) provides: the page's zone and where it came from. */
export type TimezoneScopeValue = {
  zone: string
  source: TimezoneSource
  storeId: number | undefined
  store: StoreZoneRead | undefined
  pending: boolean
  /** How many scopes enclose this one, itself included: a drawer's scope inside a page's is 2. */
  depth: number
}

export const TimezoneScopeContext = createContext<TimezoneScopeValue | null>(null)

/**
 * The zone alone, in its own context. A context re-renders every consumer
 * whenever its value changes, and the scope's value changes as its queries
 * settle (`pending`); a table that only wants the zone string must not
 * re-render — and remount its rows — for that. It changes only when the zone
 * does.
 */
export const ZoneContext = createContext<string | null>(null)

/**
 * The page's zone for `formatDateTime`; the browser's outside the app shell.
 *
 * A default rather than a throw, unlike `useDensity`: a component that shows
 * a timestamp is rendered in dialogs, drawers and tests that have no page
 * around them, and the browser's zone there is exactly what it showed before
 * zones existed.
 */
export function useZone(): string {
  return use(ZoneContext) ?? browserTimezone()
}

/** The nearest scope's zone and source; null outside a scope. */
export function useTimezoneScope(): TimezoneScopeValue | null {
  return use(TimezoneScopeContext)
}

/**
 * The sidebar module and page the reader is on, as zone keys
 * (`components/layout/timezoneMap.ts`). The shell knows the URL and provides
 * it; a page mounted in a test without the shell is on no page, and reads the
 * person's default.
 */
export type RouteZoneKeys = {
  module: string | null
  page: string | null
  /** The module's sidebar name. */
  moduleLabel?: ParseKeys
  /** The page's sidebar name. */
  pageLabel?: ParseKeys
}

export const RouteZoneContext = createContext<RouteZoneKeys>({ module: null, page: null })

export function useRouteZone(): RouteZoneKeys {
  return use(RouteZoneContext)
}

/** What a page's scope adds to its route: the store it shows, for the header. */
export type ActiveTimezoneScope = {
  storeId: number | undefined
  kind: StoreZoneKind
  depth: number
}

/**
 * How a scope tells the shell it is on screen. The shell sits above every
 * page, so it cannot read a page's context; each scope registers itself here
 * on mount and leaves on unmount, and the deepest one is the active one — a
 * drawer's store over the page beneath it.
 */
export type TimezoneHostApi = {
  enter: (id: symbol, scope: ActiveTimezoneScope) => void
  leave: (id: symbol) => void
}

export const TimezoneHostContext = createContext<TimezoneHostApi | null>(null)

export const ActiveTimezoneContext = createContext<ActiveTimezoneScope | null>(null)

/** The scope on screen, or null on a page that shows no store. */
export function useActiveTimezoneScope(): ActiveTimezoneScope | null {
  return use(ActiveTimezoneContext)
}
